import { Box, Typography, Button, Card, useTheme, TextField, CircularProgress, Grid, MenuItem, Alert, Stack, Divider } from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import InputMask from 'react-input-mask';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { visitorService } from '../../../shared/services/visitors/visitorService';
import { useToast } from '../../../shared/hooks/useToast';
import { useQuery } from '@tanstack/react-query';
import { eventService } from '../../../shared/services/tenant/eventService';

// Função para validar CPF
const isValidCPF = (cpf: string) => {
    const cleanCPF = cpf.replace(/\D/g, '');
    return cleanCPF.length === 11;
};

// Função para formatar CPF
const formatCPF = (cpf: string) => {
    const cleanCPF = cpf.replace(/\D/g, '');
    if (cleanCPF.length === 11) {
        return `${cleanCPF.slice(0,3)}.${cleanCPF.slice(3,6)}.${cleanCPF.slice(6,9)}-${cleanCPF.slice(9,11)}`;
    }
    return cpf;
};

const sensitiveFieldInputProps = {
  autoComplete: 'off',
  'data-aviva-sensitive': 'payment',
} as const;

export default function CardPaymentPage() {
    const navigate = useNavigate();
    const { id, slug } = useParams();
    const idOrSlug = slug || id;
    const theme = useTheme();
    const location = useLocation();
    const { showToast } = useToast();
    
    // Determina o caminho base (novo com slug ou antigo com id)
    const basePath = slug ? `/evento/${slug}` : `/public/event/${id}`;

    const cpf = location.state?.cpf || localStorage.getItem('lastCpf') || '';
    const [loading, setLoading] = useState(false);
    const [userIP, setUserIP] = useState('');
    const [paymentAlreadyProcessed, setPaymentAlreadyProcessed] = useState(false);
    const [boletoExistsInfo, setBoletoExistsInfo] = useState<any>(null);
    const [addressLoading, setAddressLoading] = useState(false);

    // Buscar dados do evento
    const { data: event } = useQuery({
        queryKey: ['event', idOrSlug],
        queryFn: () => eventService.getPublic(idOrSlug!).then(res => res.data),
        enabled: !!idOrSlug,
    });

    // Usa o máximo configurado no evento (padrão: 10)
    const maxInstallments = event?.max_installments_card || 10;

    const validationSchema = Yup.object().shape({
        holderName: Yup.string().required('Nome do titular obrigatório'),
        cardNumber: Yup.string()
            .required('Número do cartão obrigatório')
            .test('card-length', 'Número do cartão inválido', (value) => {
                const cleaned = value?.replace(/\D/g, '') || '';
                return cleaned.length >= 13 && cleaned.length <= 19;
            }),
        expiryMonth: Yup.string()
            .required('Mês obrigatório')
            .matches(/^(0[1-9]|1[0-2])$/, 'Mês deve estar entre 01 e 12'),
        expiryYear: Yup.string()
            .required('Ano obrigatório')
            .matches(/^20\d{2}$/, 'Ano deve estar no formato 20XX'),
        ccv: Yup.string()
            .required('CVV obrigatório')
            .matches(/^\d{3,4}$/, 'CVV deve ter 3 ou 4 dígitos'),
        installments: Yup.number().required('Parcelas obrigatórias').min(1).max(maxInstallments),
        email: Yup.string().email('E-mail inválido').required('E-mail obrigatório'),
        postalCode: Yup.string()
            .required('CEP obrigatório')
            .test('cep-valid', 'CEP inválido', (value) => {
                const cleaned = value?.replace(/\D/g, '') || '';
                return cleaned.length === 8;
            }),
        addressNumber: Yup.string().required('Número obrigatório'),
        phone: Yup.string()
            .required('Telefone obrigatório')
            .test('phone-valid', 'Telefone inválido', (value) => {
                const cleaned = value?.replace(/\D/g, '') || '';
                return cleaned.length >= 10 && cleaned.length <= 11;
            }),
        cpfCnpj: Yup.string()
            .required('CPF obrigatório')
            .test('cpf-valid', 'CPF inválido', (value) => {
                const cleaned = value?.replace(/\D/g, '') || '';
                return cleaned.length === 11;
            }),
            participantCpf: Yup.string()
            .required('CPF obrigatório')
            .test('cpf-valid', 'CPF inválido', (value) => {
                const cleaned = value?.replace(/\D/g, '') || '';
                return cleaned.length === 11;
            }),
            
    });

    // Busca o IP do usuário
    useEffect(() => {
        fetch('https://api.ipify.org?format=json')
            .then(res => res.json())
            .then(data => setUserIP(data.ip))
            .catch(() => setUserIP('127.0.0.1'));
    }, []);

    // Função para buscar endereço pelo CEP
    const buscarEnderecoPorCEP = async (cep: string, setFieldValue: any) => {
        const cleanCEP = cep.replace(/\D/g, '');
        if (cleanCEP.length !== 8) return;

        setAddressLoading(true);
        try {
            const response = await fetch(`https://viacep.com.br/ws/${cleanCEP}/json/`);
            const data = await response.json();
            
            if (!data.erro) {
                setFieldValue('address', data.logradouro || '');
                setFieldValue('neighborhood', data.bairro || '');
                setFieldValue('city', data.localidade || '');
                setFieldValue('state', data.uf || '');
            }
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
        }
        setAddressLoading(false);
    };

    const handleSubmit = async (values: any) => {
        setLoading(true);
        try {
            const paymentData = {
                cpf: values.cpfCnpj.replace(/\D/g, ''),
                eventId: event?.id || id!,
                installmentCount: values.installments,
                creditCard: {
                    holderName: values.holderName,
                    number: values.cardNumber.replace(/\D/g, ''),
                    expiryMonth: values.expiryMonth,
                    expiryYear: values.expiryYear,
                    ccv: values.ccv
                },
                creditCardHolderInfo: {
                    name: values.holderName,
                    email: values.email,
                    cpfCnpj: values.participantCpf.replace(/\D/g, ''),
                    postalCode: values.postalCode.replace(/\D/g, ''),
                    addressNumber: values.addressNumber,
                    addressComplement: values.addressComplement || '',
                    phone: values.phone.replace(/\D/g, ''),
                    mobilePhone: values.phone.replace(/\D/g, '')
                },
                remoteIp: userIP || '127.0.0.1'
            };

            const response = await visitorService.cardPayment(paymentData);
            
            // Verifica se a resposta contém payment.status === 'NOT_CREDIT_CARD'
            if (response?.payment?.status === 'NOT_CREDIT_CARD') {
                // Mostra tela de boleto existente
                setBoletoExistsInfo(response.payment);
                return;
            }

            // Verifica se há erro no processamento do cartão na resposta
            if (response?.payment?.error === 'CARD_PROCESSING_ERROR') {
                let errorMessage = response.payment.message || 'Erro no processamento do cartão';
                
                // Se há detalhes específicos dos erros, usa a primeira descrição
                if (response.payment.details?.errors && response.payment.details.errors.length > 0) {
                    errorMessage = response.payment.details.errors[0].description;
                }
                
                showToast(errorMessage, 'error');
                setLoading(false);
                return;
            }
            
            showToast('Pagamento processado com sucesso!', 'success');
            navigate(`${basePath}/success`, {
                state: {
                    cpf: values.cpfCnpj,
                    paymentMethod: 'card',
                    transactionId: response?.payment?.id,
                    value: response?.payment?.value,
                },
            });
        } catch (error: any) {
            let errorMessage = 'Erro ao processar pagamento';
            
            // Verifica se é erro de cartão com estrutura específica no catch
            if (error.response?.data?.error === 'CARD_PROCESSING_ERROR') {
                const errorData = error.response.data;
                errorMessage = errorData.message;
                
                // Se há detalhes específicos dos erros, usa a primeira descrição
                if (errorData.details?.errors && errorData.details.errors.length > 0) {
                    errorMessage = errorData.details.errors[0].description;
                }
            }
            // Verifica se é erro de pagamento já processado
            else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
                
                if (errorMessage.includes('Pagamento já foi processado') || errorMessage.includes('já foi realizado')) {
                    setPaymentAlreadyProcessed(true);
                    setLoading(false);
                    return;
                }
            }
            
            showToast(errorMessage, 'error');
        }
        setLoading(false);
    };

    // Se o pagamento já foi processado, mostra tela específica
    if (paymentAlreadyProcessed) {
        return (
            <Box sx={{ p: { xs: 1, md: 4 }, background: theme.palette.background.default, minHeight: '100vh' }}>
                <Card sx={{
                    background: theme.palette.background.paper,
                    borderRadius: 4,
                    p: 4,
                    maxWidth: 600,
                    margin: '32px auto',
                    boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
                    textAlign: 'center'
                }}>
                    <Stack alignItems="center" spacing={3} sx={{ py: 4 }}>
                        <CheckCircleIcon sx={{ fontSize: 80, color: 'info.main' }} />
                        <Typography variant="h4" fontWeight={700} color="info.main">
                            Pagamento já processado!
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                            O pagamento para este participante já foi realizado e está sendo processado.
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Status: <strong>Confirmado</strong>
                        </Typography>
                        <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
                            <Button
                                variant="outlined"
                                onClick={() => navigate(`${basePath}/payment`)}
                            >
                                Voltar
                            </Button>
                            <Button
                                variant="contained"
                                color="info"
                                onClick={() => navigate(basePath)}
                            >
                                Nova inscrição
                            </Button>
                        </Stack>
                    </Stack>
                </Card>
            </Box>
        );
    }

    // Se já existe boleto, mostra tela específica
    if (boletoExistsInfo) {
        return (
            <Box sx={{ p: { xs: 1, md: 4 }, background: theme.palette.background.default, minHeight: '100vh' }}>
                <Card sx={{
                    background: theme.palette.background.paper,
                    borderRadius: 4,
                    p: 4,
                    maxWidth: 600,
                    margin: '32px auto',
                    boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)',
                    textAlign: 'center'
                }}>
                    <Stack alignItems="center" spacing={3} sx={{ py: 4 }}>
                        <CreditCardIcon sx={{ fontSize: 80, color: 'warning.main' }} />
                        <Typography variant="h4" fontWeight={700} color="warning.main">
                            Boleto já existe
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 400 }}>
                            {boletoExistsInfo.message || 'Já existe um boleto ativo para este participante. Não é possível processar pagamento com cartão.'}
                        </Typography>
                        {boletoExistsInfo.details && (
                            <Box sx={{ textAlign: 'center', mt: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Valor:</strong> {boletoExistsInfo.details.value}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Vencimento:</strong> {boletoExistsInfo.details.dueDate}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Status:</strong> {boletoExistsInfo.details.status === 'PENDING' ? 'Pendente' : boletoExistsInfo.details.status}
                                </Typography>
                            </Box>
                        )}
                        {boletoExistsInfo.bankSlipUrl && (
                            <Button
                                variant="contained"
                                color="warning"
                                sx={{ mb: 2 }}
                                onClick={() => window.open(boletoExistsInfo.bankSlipUrl, '_blank')}
                            >
                                Visualizar Boleto
                            </Button>
                        )}
                        <Stack direction="row" spacing={2}>
                            <Button
                                variant="contained"
                                onClick={() => navigate(basePath)}
                            >
                                Nova inscrição
                            </Button>
                        </Stack>
                    </Stack>
                </Card>
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 1, md: 4 }, background: theme.palette.background.default, minHeight: '100vh' }}>
            <Card sx={{
                background: theme.palette.background.paper,
                borderRadius: 4,
                p: 4,
                maxWidth: 800,
                margin: '32px auto',
                boxShadow: '0 2px 8px 0 rgba(16,30,54,0.04)'
            }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 4, textAlign: 'center', justifyContent: 'center' }}>
                    <CreditCardIcon sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Typography variant="h4" fontWeight={700} color="primary.main">
                        Pagamento com Cartão
                    </Typography>
                </Stack>

                <Formik
                    initialValues={{
                        holderName: '',
                        cardNumber: '',
                        expiryMonth: '',
                        expiryYear: '',
                        ccv: '',
                        installments: 1,
                        email: '',
                        postalCode: '',
                        address: '',
                        neighborhood: '',
                        city: '',
                        state: '',
                        addressNumber: '',
                        addressComplement: '',
                        phone: '',
                        participantCpf: '',
                        cpfCnpj: formatCPF(cpf), // Formata o CPF inicial
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, errors, touched, handleChange, handleBlur, setFieldValue }) => (
                        <Form>
                            <Grid container spacing={3}>
                                {/* Dados do Cartão */}
                                <Grid item xs={12}>
                                    <Typography variant="h6" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <CreditCardIcon /> Dados do Cartão
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                </Grid>

                                <Grid item xs={6}>
                                    <TextField
                                        fullWidth
                                        name="holderName"
                                        label="Nome completo do titular"
                                        value={values.holderName}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.holderName && !!errors.holderName}
                                        helperText={touched.holderName && errors.holderName}
                                        placeholder="Como está no cartão"
                                        inputProps={sensitiveFieldInputProps}
                                    />
                                </Grid>

                                <Grid item xs={6} md={6}>
                                    <InputMask
                                        mask="999.999.999-99"
                                        value={values.participantCpf}
                                        onChange={(e) => setFieldValue('participantCpf', e.target.value)}
                                        onBlur={handleBlur}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                name="participantCpf"
                                                label="CPF do titular"
                                                error={touched.participantCpf && !!errors.participantCpf}
                                                helperText={touched.participantCpf && errors.participantCpf}
                                                placeholder="000.000.000-00"
                                            />
                                        )}
                                    </InputMask>
                                </Grid>
                                <Grid item xs={12} md={8}>
                                    <InputMask
                                        mask="9999 9999 9999 9999"
                                        value={values.cardNumber}
                                        onChange={(e) => setFieldValue('cardNumber', e.target.value)}
                                        onBlur={handleBlur}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                name="cardNumber"
                                                label="Número do cartão"
                                                error={touched.cardNumber && !!errors.cardNumber}
                                                helperText={touched.cardNumber && errors.cardNumber}
                                                placeholder="0000 0000 0000 0000"
                                                inputProps={sensitiveFieldInputProps}
                                            />
                                        )}
                                    </InputMask>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <TextField
                                        fullWidth
                                        select
                                        name="installments"
                                        label="Parcelas"
                                        value={values.installments}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.installments && !!errors.installments}
                                        helperText={touched.installments && errors.installments}
                                    >
                                        {Array.from({ length: maxInstallments }, (_, i) => i + 1).map(num => (
                                            <MenuItem key={num} value={num}>
                                                {num}x sem juros
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        name="expiryMonth"
                                        label="Mês"
                                        value={values.expiryMonth}
                                        onChange={(e) => {
                                            let value = e.target.value.replace(/\D/g, '');
                                            if (value.length === 1 && parseInt(value) > 1) {
                                                value = '0' + value;
                                            }
                                            if (value.length === 2 && parseInt(value) > 12) {
                                                value = '12';
                                            }
                                            setFieldValue('expiryMonth', value);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.expiryMonth && !!errors.expiryMonth}
                                        helperText={touched.expiryMonth && errors.expiryMonth}
                                        placeholder="MM"
                                        inputProps={{ ...sensitiveFieldInputProps, maxLength: 2 }}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        name="expiryYear"
                                        label="Ano"
                                        value={values.expiryYear}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setFieldValue('expiryYear', value);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.expiryYear && !!errors.expiryYear}
                                        helperText={touched.expiryYear && errors.expiryYear}
                                        placeholder="2025"
                                        inputProps={{ ...sensitiveFieldInputProps, maxLength: 4 }}
                                    />
                                </Grid>

                                <Grid item xs={4}>
                                    <TextField
                                        fullWidth
                                        name="ccv"
                                        label="CVV"
                                        value={values.ccv}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setFieldValue('ccv', value);
                                        }}
                                        onBlur={handleBlur}
                                        error={touched.ccv && !!errors.ccv}
                                        helperText={touched.ccv && errors.ccv}
                                        placeholder="000"
                                        inputProps={{ ...sensitiveFieldInputProps, maxLength: 4 }}
                                    />
                                </Grid>

                                {/* Dados Pessoais */}
                                <Grid item xs={12} sx={{ mt: 3 }}>
                                    <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                                        Dados Pessoais
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        name="email"
                                        label="E-mail"
                                        type="email"
                                        value={values.email}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.email && !!errors.email}
                                        helperText={touched.email && errors.email}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <InputMask
                                        mask="(99) 99999-9999"
                                        value={values.phone}
                                        onChange={(e) => setFieldValue('phone', e.target.value)}
                                        onBlur={handleBlur}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                name="phone"
                                                label="Telefone/Celular"
                                                error={touched.phone && !!errors.phone}
                                                helperText={touched.phone && errors.phone}
                                                placeholder="(11) 99999-9999"
                                            />
                                        )}
                                    </InputMask>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <InputMask
                                        mask="999.999.999-99"
                                        value={values.cpfCnpj}
                                        onChange={(e) => setFieldValue('cpfCnpj', e.target.value)}
                                        onBlur={handleBlur}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                name="cpfCnpj"
                                                label="CPF do participante"
                                                error={touched.cpfCnpj && !!errors.cpfCnpj}
                                                helperText={touched.cpfCnpj && errors.cpfCnpj}
                                                placeholder="000.000.000-00"
                                            />
                                        )}
                                    </InputMask>
                                </Grid>

                                {/* Endereço */}
                                <Grid item xs={12} sx={{ mt: 3 }}>
                                    <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                                        Endereço de Cobrança
                                    </Typography>
                                    <Divider sx={{ mb: 2 }} />
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <InputMask
                                        mask="99999-999"
                                        value={values.postalCode}
                                        onChange={(e) => {
                                            setFieldValue('postalCode', e.target.value);
                                            if (e.target.value.replace(/\D/g, '').length === 8) {
                                                buscarEnderecoPorCEP(e.target.value, setFieldValue);
                                            }
                                        }}
                                        onBlur={handleBlur}
                                    >
                                        {(inputProps: any) => (
                                            <TextField
                                                {...inputProps}
                                                fullWidth
                                                name="postalCode"
                                                label="CEP"
                                                error={touched.postalCode && !!errors.postalCode}
                                                helperText={touched.postalCode && errors.postalCode}
                                                placeholder="00000-000"
                                                InputProps={{
                                                    endAdornment: addressLoading && <CircularProgress size={20} />
                                                }}
                                            />
                                        )}
                                    </InputMask>
                                </Grid>

                                <Grid item xs={12} md={8}>
                                    <TextField
                                        fullWidth
                                        name="address"
                                        label="Endereço"
                                        value={values.address}
                                        onChange={handleChange}
                                        disabled
                                        placeholder="Será preenchido automaticamente"
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        name="addressNumber"
                                        label="Número"
                                        value={values.addressNumber}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={touched.addressNumber && !!errors.addressNumber}
                                        helperText={touched.addressNumber && errors.addressNumber}
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        name="addressComplement"
                                        label="Complemento"
                                        value={values.addressComplement}
                                        onChange={handleChange}
                                        placeholder="Apto, Bloco, etc"
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        name="neighborhood"
                                        label="Bairro"
                                        value={values.neighborhood}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        fullWidth
                                        name="city"
                                        label="Cidade"
                                        value={values.city}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </Grid>

                                {/* Resumo do Pagamento com Taxa */}
                                {event && (
                                    <Grid item xs={12} sx={{ mt: 3 }}>
                                        <Card sx={{ p: 2, bgcolor: 'grey.50', border: '1px solid', borderColor: 'grey.200' }}>
                                            <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                                                Resumo do Pagamento
                                            </Typography>
                                            <Divider sx={{ mb: 2 }} />
                                            
                                            {(() => {
                                                // Calcular taxa do cartão
                                                const cardFee = event.card_fee ? Number(event.card_fee) : 0;
                                                const cardFeePercent = event.card_fee_percent ? Number(event.card_fee_percent) : 0;
                                                const originalValue = 0; // Não temos acesso ao valor do participante aqui
                                                const percentFee = (originalValue * cardFeePercent) / 100;
                                                const totalFee = cardFee + percentFee;
                                                const hasFee = cardFee > 0 || cardFeePercent > 0;

                                                return (
                                                    <>
                                                        {hasFee && (
                                                            <Alert severity="info" sx={{ mb: 2 }}>
                                                                <Typography variant="body2">
                                                                    <strong>Taxa do Cartão:</strong>{' '}
                                                                    {cardFee > 0 && `R$ ${cardFee.toFixed(2).replace('.', ',')}`}
                                                                    {cardFee > 0 && cardFeePercent > 0 && ' + '}
                                                                    {cardFeePercent > 0 && `${cardFeePercent.toFixed(1).replace('.', ',')}% sobre o valor`}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary">
                                                                    A taxa será adicionada ao valor total do pagamento.
                                                                </Typography>
                                                            </Alert>
                                                        )}
                                                        <Typography variant="body2" color="text.secondary">
                                                            O valor exato será calculado com base no ingresso selecionado durante a inscrição.
                                                        </Typography>
                                                    </>
                                                );
                                            })()}
                                        </Card>
                                    </Grid>
                                )}

                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
                                        <Button
                                            variant="outlined"
                                            size="large"
                                            onClick={() => navigate(`${basePath}/payment`)}
                                            disabled={loading}
                                        >
                                            Voltar
                                        </Button>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            size="large"
                                            disabled={loading}
                                            startIcon={loading && <CircularProgress size={20} />}
                                            sx={{ minWidth: 200 }}
                                        >
                                            {loading ? 'Processando...' : 'Finalizar Pagamento'}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </Form>
                    )}
                </Formik>
            </Card>
        </Box>
    );
}